import { useMemo } from "react";
import {
    UseQueryResult,
    useQuery,
    useQueryClient,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import { fetchDiagnosisCodes } from "../../services";
import { filterDiagnosisCodesBySearchTerm } from "../../utils/helpers/diagnosis";
import { IDiagnosisCode } from "../../utils/types";
import { IApiResponse } from "../../utils/types/api-response";

interface DiagnosisCodesQueryOptions {
    enabled?: boolean;
    onSuccess?: (response: IApiResponse<IDiagnosisCode[]>) => void;
}

type DiagnosisCodeSearchResult = UseQueryResult<
    IApiResponse<IDiagnosisCode[]>,
    AxiosError<IApiResponse<IDiagnosisCode[]>>
> & {
    cachedDiagnosisCodesForSelectedValues?: IDiagnosisCode[];
};

const getDiagnosisCodesQueryKey = (
    showDeprecatedCode?: boolean,
    searchTerm?: string
) => ["diagnosis-codes", showDeprecatedCode, searchTerm] as const;

// Handles backend requests and exact-key caching through React Query.
function useDiagnosisCodesQuery(
    showDeprecatedCode?: boolean,
    searchTerm?: string,
    options: DiagnosisCodesQueryOptions = {}
) {
    return useQuery<
        IApiResponse<IDiagnosisCode[]>,
        AxiosError<IApiResponse<IDiagnosisCode[]>>
    >({
        queryKey: getDiagnosisCodesQueryKey(showDeprecatedCode, searchTerm),
        queryFn: ({ signal }) =>
            fetchDiagnosisCodes(showDeprecatedCode, searchTerm, signal),
        enabled: options.enabled,
        onSuccess: options.onSuccess,
        staleTime: Infinity,
    });
}

// Searches the cached base list first and calls the backend only on a local miss.
export function useFetchDiagnosisCodes(
    showDeprecatedCode?: boolean,
    searchTerm?: string
): DiagnosisCodeSearchResult {
    const queryClient = useQueryClient();
    const normalizedSearchTerm = searchTerm?.trim().toLowerCase() || undefined;
    const baseQuery = useDiagnosisCodesQuery(showDeprecatedCode);
    const cachedDiagnosisCodes = baseQuery.data?.data;
    const localMatches = useMemo(
        () =>
            filterDiagnosisCodesBySearchTerm(
                cachedDiagnosisCodes,
                normalizedSearchTerm
            ),
        [cachedDiagnosisCodes, normalizedSearchTerm]
    );
    const hasLocalMatches = localMatches.length > 0;
    const hasMinimumSearchLength = (normalizedSearchTerm?.length ?? 0) >= 2;
    const shouldSearchBackend =
        hasMinimumSearchLength && baseQuery.isSuccess && !hasLocalMatches;
    const searchQuery = useDiagnosisCodesQuery(
        showDeprecatedCode,
        normalizedSearchTerm ?? "",
        {
            enabled: shouldSearchBackend,
            onSuccess: (response) => {
                queryClient.setQueryData<IApiResponse<IDiagnosisCode[]>>(
                    getDiagnosisCodesQueryKey(showDeprecatedCode),
                    (cachedResponse) => {
                        if (!cachedResponse) return cachedResponse;
                        const diagnosisCodesByCode = new Map(
                            cachedResponse.data.map((diagnosis) => [
                                diagnosis.code,
                                diagnosis,
                            ])
                        );
                        response.data.forEach((diagnosis) => {
                            diagnosisCodesByCode.set(diagnosis.code, diagnosis);
                        });
                        return {
                            ...cachedResponse,
                            data: Array.from(
                                diagnosisCodesByCode.values()
                            ).sort((first, second) =>
                                first.code.localeCompare(second.code)
                            ),
                        };
                    }
                );
            },
        }
    );

    let { data } = baseQuery;
    if (normalizedSearchTerm) {
        data =
            hasLocalMatches && baseQuery.data
                ? { ...baseQuery.data, data: localMatches }
                : searchQuery.data;
    }
    const activeQuery = shouldSearchBackend ? searchQuery : baseQuery;

    return {
        ...activeQuery,
        data,
        isLoading:
            baseQuery.isLoading ||
            (shouldSearchBackend && searchQuery.isLoading),
        isFetching: normalizedSearchTerm
            ? shouldSearchBackend && searchQuery.isFetching
            : baseQuery.isFetching,
        error:
            baseQuery.error ?? (shouldSearchBackend ? searchQuery.error : null),
        cachedDiagnosisCodesForSelectedValues: cachedDiagnosisCodes,
    } as DiagnosisCodeSearchResult;
}
